import React, { useEffect, useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  CircularProgress,
  LinearProgress,
  Modal,
  Fade,
  Backdrop
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";

const UpdatePasswordPage = () => {
  const [userData, setUserData] = useState({
    nombre: "",
    telefono: "",
    email: ""
  });
  const [loading, setLoading] = useState(true);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetch("/sistema/get-user-info/", { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        setUserData({
          nombre: data.nombre,
          telefono: data.telefono,
          email: data.email
        });
        setLoading(false);
      })
      .catch(() => {
        setError("Error al obtener datos del usuario.");
        setLoading(false);
      });
  }, []);

  const fetchCSRFToken = async () => {
    try {
      const res = await fetch("/sistema/get-csrf-token/", {
        method: "GET",
        credentials: "include"
      });
      const data = await res.json();
      return data.csrfToken;
    } catch {
      return null;
    }
  };

  const validatePassword = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    setPasswordStrength(strength);
    return strength >= 3;
  };

  const handleSubmit = async () => {
    setError("");
    if (!userData.nombre || !userData.telefono) {
      setError("Nombre y teléfono son obligatorios.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    if (!validatePassword(newPassword)) {
      setError("La contraseña no cumple con los requisitos de seguridad.");
      return;
    }

    const csrfToken = await fetchCSRFToken();
    if (!csrfToken) {
      setError("No se pudo obtener el token CSRF.");
      return;
    }

    try {
      const res = await fetch("/sistema/update-password/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken
        },
        credentials: "include",
        body: JSON.stringify({
          email: userData.email,
          nombre: userData.nombre,
          telefono: userData.telefono,
          password1: newPassword,
          password2: confirmPassword
        })
      });

      const data = await res.json();
      if (res.ok) {
        setSuccessModalOpen(true);
      } else {
        setError(data.error || "Error al actualizar datos.");
      }
    } catch (err) {
      setError("Error interno del servidor.");
    }
  };

  const getPasswordColor = () => {
    switch (passwordStrength) {
      case 1: return "red";
      case 2: return "orange";
      case 3: return "yellow";
      case 4: return "lightgreen";
      case 5: return "green";
      default: return "grey";
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Bienvenido/a a Hydro Luz
      </Typography>
      <Typography variant="body1" mb={1}>
        Confirma tus datos y actualiza tu contraseña para completar tu registro.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Correo registrado: <strong>{userData.email}</strong>
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {error && (
            <Typography color="error" sx={{ mb: 1 }}>{error}</Typography>
          )}

          <TextField
            label="Nombre"
            value={userData.nombre}
            onChange={(e) => setUserData({ ...userData, nombre: e.target.value })}
            fullWidth
          />
          <TextField
            label="Teléfono"
            value={userData.telefono}
            onChange={(e) => setUserData({ ...userData, telefono: e.target.value })}
            fullWidth
          />

          <TextField
            label="Nueva Contraseña"
            type={showPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              validatePassword(e.target.value);
            }}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((prev) => !prev)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <LinearProgress
            variant="determinate"
            value={(passwordStrength / 5) * 100}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: "#ddd",
              "& .MuiLinearProgress-bar": { backgroundColor: getPasswordColor() }
            }}
          />

          <TextField
            label="Confirmar Contraseña"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
          />

          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              mt: 2,
              fontWeight: "bold",
              backgroundColor: "#006064",
              "&:hover": { backgroundColor: "#004d4d" }
            }}
          >
            GUARDAR Y CONTINUAR
          </Button>
        </Box>
      )}

      <Modal
        open={successModalOpen}
        onClose={() => navigate("/login")}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300 }}
      >
        <Fade in={successModalOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "white",
              borderRadius: 3,
              boxShadow: 24,
              p: 4,
              maxWidth: 400,
              width: "90%",
              textAlign: "center"
            }}
          >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              ¡Datos actualizados correctamente!
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Tu contraseña ha sido cambiada exitosamente. Ahora puedes iniciar sesión con tus nuevas credenciales.
            </Typography>
            <Button
              onClick={() => navigate("/login")}
              variant="contained"
              sx={{
                backgroundColor: "#006064",
                "&:hover": { backgroundColor: "#004d4d" },
                fontWeight: "bold",
                px: 3
              }}
            >
              IR AL LOGIN
            </Button>
          </Box>
        </Fade>
      </Modal>
    </Container>
  );
};

export default UpdatePasswordPage;
